import { Head, Link } from '@inertiajs/react';
import ReactMarkdown from 'react-markdown';
import { MainLayout } from '../../layouts/MainLayout';
import { route } from '../../utils/route';

interface Recipe {
  id: number;
  name: string;
  slug: string;
  steps: string | null;
  image_url: string | null;
  description: string;
}

interface RecipePack {
  id: number;
  title: string;
  slug: string | null;
  image_url: string | null;
  calculated_price: number;
}

interface RecipeShowProps {
  recipe: Recipe;
  pack: RecipePack | null;
  appUrl: string;
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

const markdownComponents = {
  h1: ({ ...props }) => <h1 className="mb-4 mt-6 text-2xl font-bold text-gray-900" {...props} />,
  h2: ({ ...props }) => <h2 className="mb-3 mt-6 text-xl font-bold text-gray-900" {...props} />,
  h3: ({ ...props }) => <h3 className="mb-2 mt-5 text-lg font-semibold text-gray-900" {...props} />,
  p: ({ ...props }) => <p className="mb-4 leading-relaxed text-gray-700" {...props} />,
  ul: ({ ...props }) => <ul className="mb-4 ml-6 list-disc space-y-1.5 text-gray-700" {...props} />,
  ol: ({ ...props }) => <ol className="mb-4 ml-6 list-decimal space-y-1.5 text-gray-700" {...props} />,
  li: ({ ...props }) => <li className="leading-relaxed" {...props} />,
  strong: ({ ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
  em: ({ ...props }) => <em className="italic" {...props} />,
  a: ({ ...props }) => (
    <a className="text-green-600 underline hover:text-green-700" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  blockquote: ({ ...props }) => (
    <blockquote className="my-4 border-l-4 border-green-500 bg-green-50 px-4 py-2 italic text-gray-700" {...props} />
  ),
  code: ({ ...props }) => <code className="rounded bg-gray-100 px-1.5 py-0.5 text-sm text-gray-800" {...props} />,
  hr: ({ ...props }) => <hr className="my-6 border-gray-200" {...props} />,
};

export default function RecipeShow({ recipe, pack, appUrl }: RecipeShowProps) {
  const pageTitle = `${recipe.name} | Recetas | Abasto Virtual`;
  const pageDescription = recipe.description;
  const pageUrl = `${appUrl}${route('recipes.show', { slug: recipe.slug })}`;
  const pageImage = recipe.image_url || pack?.image_url || `${appUrl}/appicons/og-image.jpg`;

  return (
    <MainLayout>
      <Head>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={recipe.name} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:locale" content="es_AR" />
        <meta property="og:site_name" content="Abasto Virtual" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={recipe.name} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={pageImage} />
      </Head>

      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          {recipe.image_url && (
            <div className="aspect-[16/9] w-full overflow-hidden bg-gray-100">
              <img
                src={recipe.image_url}
                alt={recipe.name}
                className="h-full w-full object-cover"
              />
            </div>
          )}

          <div className="p-5 md:p-8">
            <h1 className="m-0 mb-2 text-3xl font-bold text-gray-900 md:text-4xl">
              {recipe.name}
            </h1>
            <p className="m-0 mb-6 text-sm text-gray-500">Receta exclusiva de Abasto Virtual</p>

            {recipe.steps ? (
              <div className="text-[15px]">
                <ReactMarkdown components={markdownComponents}>{recipe.steps}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-gray-500 italic">Esta receta todavía no tiene pasos publicados.</p>
            )}

            {(recipe.slug === 'tortilla-de-papas' || recipe.slug === 'fajitas-mixtas') && (
              <p className="mt-6 text-sm text-gray-700">
                <Link href="/bolson-de-verduras" className="font-semibold text-green-700 underline hover:text-green-800">
                  El bolsón de verduras: qué trae, cuánto dura y cuánto sale puesto en casa
                </Link>
              </p>
            )}
          </div>

          {pack && (
            <div className="border-t border-gray-200 bg-gradient-to-br from-green-50 to-amber-50 p-5 md:p-8">
              <div className="flex flex-col gap-4 md:flex-row md:items-center">
                <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-lg bg-white shadow-sm md:h-32 md:w-32">
                  {pack.image_url ? (
                    <img src={pack.image_url} alt={pack.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-3xl text-gray-300">
                      <i className="fa fa-box" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="m-0 mb-1 text-xl font-bold text-gray-900 md:text-2xl">
                    Conseguí todos los ingredientes usando el Pack {pack.title}
                  </h2>
                  <p className="m-0 mb-4 text-sm text-gray-600">
                    Todo lo que necesitás para esta receta en un solo click. {formatPrice(pack.calculated_price)}.
                  </p>
                  <Link
                    href={route('packs.show', { id: pack.slug || pack.id })}
                    className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-5 py-3 text-sm font-semibold text-white no-underline shadow-sm transition-colors hover:bg-green-700"
                  >
                    <i className="fa fa-shopping-cart" />
                    Ver el pack
                    <i className="fa fa-arrow-right" />
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 bg-gray-50 p-5 md:p-8">
            <div className="flex flex-col items-center gap-3 text-center md:flex-row md:justify-between md:text-left">
              <div>
                <h3 className="m-0 mb-1 text-lg font-bold text-gray-900">
                  ¿Tenés una receta para compartir?
                </h3>
                <p className="m-0 text-sm text-gray-600">
                  Envianos tu receta y la publicamos en Abasto Virtual.
                </p>
              </div>
              <Link
                href={route('recipes.create')}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-3 text-sm font-semibold text-white no-underline shadow-sm transition-colors hover:bg-amber-600"
              >
                <i className="fa fa-paper-plane" />
                Envianos tu receta
              </Link>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
